import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { extractApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function AdminLogin() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user?.role === "admin") navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post("/admin/auth/login", { username: username.trim(), password });
      toast.success("Welcome back, admin.");
      await refresh();
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(extractApiError(err, "Invalid username or password."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-16 bg-stone-50">
      <div
        data-testid="admin-login-card"
        className="bg-white border border-stone-200 rounded-md p-10 sm:p-14 max-w-md w-full shadow-sm"
      >
        <div className="flex justify-center">
          <span className="w-14 h-14 rounded-full bg-emerald-900 flex items-center justify-center">
            <ShieldCheck className="text-stone-50" />
          </span>
        </div>
        <h1 className="mt-8 font-serif text-3xl text-stone-900 text-center">
          Administrator Portal
        </h1>
        <p className="mt-3 text-center text-sm text-stone-600 font-sans">
          Azure Gypsy Farms — staff access only.
        </p>

        <form onSubmit={onSubmit} className="mt-8 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="admin-username" className="font-sans text-xs uppercase tracking-[0.2em] text-stone-600">
              Username
            </Label>
            <Input
              id="admin-username"
              data-testid="admin-username-input"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Azuregypsy"
              required
              className="font-sans"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="admin-password" className="font-sans text-xs uppercase tracking-[0.2em] text-stone-600">
              Password
            </Label>
            <Input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="font-sans"
            />
          </div>

          <Button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit-btn"
            className="w-full bg-emerald-900 hover:bg-emerald-800 text-stone-50 py-6 rounded-md"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in to Admin"}
          </Button>
        </form>

        <p className="mt-6 text-center text-[11px] uppercase tracking-[0.2em] text-stone-500">
          Restricted · Audit-logged · Lockout after 5 fails
        </p>
        <p className="mt-3 text-center text-xs font-sans text-stone-500">
          Not an admin?{" "}
          <a href="/login" className="underline text-emerald-900" data-testid="admin-login-back-link">
            Member sign-in
          </a>
        </p>
      </div>
    </div>
  );
}
